import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ShippingQuote from "@/components/ShippingQuote";
import { Check, X } from "lucide-react";

const PAYMENTS = [
  { v: "pix", l: "Pix", d: "Pagamento imediato — 5% de desconto com cupom PIXDAÁUREA" },
  { v: "cartao", l: "Cartão de crédito", d: "Enviamos um link de pagamento após o pedido" },
  { v: "devido", l: "Devido (a combinar)", d: "Pagamento combinado por WhatsApp" },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addr, setAddr] = useState({
    label: "Endereço", recipient: "", zip: "", street: "", number: "",
    complement: "", district: "", city: "", state: "",
  });
  const [notes, setNotes] = useState("");
  const [shipping, setShipping] = useState(null);
  const [payment, setPayment] = useState("pix");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?redirect=/checkout"); return; }
    if (items.length === 0) { navigate("/carrinho"); return; }
    api.get("/me/addresses").then(({ data }) => {
      setSavedAddresses(data || []);
      if (data && data[0]) setAddr({ ...data[0] });
    }).catch(() => {});
    setAddr((a) => ({ ...a, recipient: user.name }));
  }, [user, loading, items.length, navigate]);

  // Re-validate coupon whenever payment or subtotal changes
  useEffect(() => {
    if (!coupon?.code) return;
    applyCoupon(coupon.code, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, subtotal]);

  const fillByCep = async (cep) => {
    const c = cep.replace(/\D/g, "");
    if (c.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (d.erro) return;
      setAddr((a) => ({ ...a, zip: c, street: d.logradouro || a.street, district: d.bairro || a.district, city: d.localidade || a.city, state: d.uf || a.state }));
    } catch { /* noop */ }
  };

  const applyCoupon = async (codeArg, silent = false) => {
    const code = (codeArg ?? couponCode).trim();
    if (!code) { setCouponError("Informe um código"); return; }
    setApplying(true); setCouponError("");
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal, payment_method: payment });
      if (data.valid) {
        setCoupon({ code: data.code || code, discount_pct: data.discount_pct, discount_amount: data.discount_amount });
        if (!silent) toast.success(data.message);
      } else {
        setCoupon(null);
        setCouponError(data.message || "Cupom inválido");
      }
    } catch (err) {
      setCoupon(null);
      const msg = err?.response?.data?.detail || err?.message || "Erro ao validar cupom";
      setCouponError(msg);
    } finally { setApplying(false); }
  };

  const removeCoupon = () => { setCoupon(null); setCouponCode(""); setCouponError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!addr.zip || !addr.street || !addr.number || !addr.city || !addr.state) {
      toast.error("Preencha o endereço de entrega");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, quantity: i.quantity,
          size: i.size, variation: i.variation, pair: i.pair || null,
        })),
        shipping_address: addr,
        notes,
        shipping_cost: shipping?.price ?? null,
        shipping_option: shipping ? { id: shipping.id, name: shipping.name, company: shipping.company, price: shipping.price, delivery_time: shipping.delivery_time } : null,
        payment_method: payment,
        coupon_code: coupon?.code || null,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      toast.success(`Pedido ${data.order_number} criado!`);
      navigate(`/conta?tab=pedidos&destaque=${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao finalizar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  const shippingCost = shipping?.price || 0;
  const discount = coupon?.discount_amount || 0;
  const total = Math.max(0, subtotal + shippingCost - discount);

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="checkout-page">
      <h1 className="font-display text-4xl text-[#49392F] mb-10">Finalizar pedido</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-8">
          {savedAddresses.length > 0 && (
            <div>
              <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4">Endereços salvos</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {savedAddresses.map((a) => (
                  <button type="button" key={a.id} onClick={() => setAddr({ ...a })} className={`text-left p-4 border ${addr.id === a.id ? "border-[#49392F]" : "border-[#E8DCCB]"} bg-white`}>
                    <div className="text-[13px] text-[#49392F]">{a.street}, {a.number}</div>
                    <div className="text-[12px] text-[#49392F]/70">{a.district} — {a.city}/{a.state}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4">Endereço de entrega</h3>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="CEP" className="duo-input col-span-2 sm:col-span-1" value={addr.zip} onChange={(e) => { setAddr({...addr, zip: e.target.value}); fillByCep(e.target.value); }} data-testid="input-zip" />
              <input placeholder="Para quem?" className="duo-input col-span-2 sm:col-span-1" value={addr.recipient || ""} onChange={(e) => setAddr({...addr, recipient: e.target.value})} />
              <input required placeholder="Rua" className="duo-input col-span-2" value={addr.street} onChange={(e) => setAddr({...addr, street: e.target.value})} data-testid="input-street" />
              <input required placeholder="Número" className="duo-input col-span-2 sm:col-span-1" value={addr.number} onChange={(e) => setAddr({...addr, number: e.target.value})} data-testid="input-number" />
              <input placeholder="Complemento" className="duo-input col-span-2 sm:col-span-1" value={addr.complement || ""} onChange={(e) => setAddr({...addr, complement: e.target.value})} />
              <input placeholder="Bairro" className="duo-input col-span-2 sm:col-span-1" value={addr.district} onChange={(e) => setAddr({...addr, district: e.target.value})} />
              <input required placeholder="Cidade" className="duo-input col-span-2 sm:col-span-1" value={addr.city} onChange={(e) => setAddr({...addr, city: e.target.value})} data-testid="input-city" />
              <input required placeholder="Estado (UF)" className="duo-input col-span-2 sm:col-span-1" value={addr.state} onChange={(e) => setAddr({...addr, state: e.target.value.toUpperCase().slice(0,2)})} data-testid="input-state" />
            </div>
          </div>

          <ShippingQuote items={items} cep={addr.zip} onSelect={setShipping} selected={shipping} />

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4">Forma de pagamento</h3>
            <div className="space-y-2">
              {PAYMENTS.map((p) => (
                <label key={p.v} className={`flex items-start gap-3 p-4 border cursor-pointer ${payment === p.v ? "border-[#49392F] bg-[#E8DCCB]/40" : "border-[#E8DCCB] bg-white"}`} data-testid={`payment-${p.v}`}>
                  <input type="radio" name="payment" value={p.v} checked={payment === p.v} onChange={() => setPayment(p.v)} className="mt-1" />
                  <div>
                    <div className="text-[13px] text-[#49392F] font-medium">{p.l}</div>
                    <div className="text-[11px] text-[#49392F]/70">{p.d}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4">Cupom de desconto</h3>
            {coupon ? (
              <div className="flex items-center justify-between bg-[#E8DCCB]/40 border border-[#C2A064]/40 p-4">
                <div>
                  <div className="text-[13px] text-[#49392F] font-medium flex items-center gap-2"><Check size={14} className="text-[#C2A064]" /> {coupon.code} aplicado</div>
                  <div className="text-[12px] text-[#49392F]/70 mt-1">{coupon.discount_pct}% de desconto = {formatBRL(coupon.discount_amount)}</div>
                </div>
                <button type="button" onClick={removeCoupon} className="text-[#49392F]/60 hover:text-[#49392F]" aria-label="remover cupom"><X size={16} /></button>
              </div>
            ) : (
              <div>
                <div className="flex gap-2">
                  <input className="duo-input flex-1" placeholder="Ex.: BRILHE15" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} data-testid="coupon-input" />
                  <button type="button" disabled={applying} onClick={() => applyCoupon()} className="duo-btn duo-btn-secondary" data-testid="coupon-apply">
                    {applying ? "..." : "Aplicar"}
                  </button>
                </div>
                {couponError && <p className="text-[12px] text-red-700 mt-2">{couponError}</p>}
                <div className="text-[11px] text-[#49392F]/60 mt-2">
                  Cupons ativos: <b>PIXDAÁUREA</b> (5% Pix) · <b>BRILHE15</b> (15% em pedidos acima de R$ 500) · <b>EXCLUSIVA35</b> (35% acima de R$ 1.000)
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4">Observações (opcional)</h3>
            <textarea rows={3} className="duo-input" placeholder="Alguma preferência de embalagem, horário de contato..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <aside className="bg-white border border-[#C2A064]/20 p-8 h-fit lg:sticky lg:top-32">
          <h3 className="font-display text-xl text-[#49392F]">Resumo</h3>
          <div className="hairline my-5" />
          <div className="space-y-3 mb-5">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.size}-${i.variation}`} className="flex justify-between text-[13px] text-[#49392F]">
                <span className="truncate pr-2">{i.quantity}x {i.name}</span>
                <span>{formatBRL(i.unit_price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="hairline my-5" />
          <div className="flex justify-between text-[14px] text-[#49392F] mb-2"><span>Subtotal</span><span>{formatBRL(subtotal)}</span></div>
          <div className="flex justify-between text-[14px] text-[#49392F] mb-2"><span>Frete</span><span>{shipping ? formatBRL(shipping.price) : "a combinar"}</span></div>
          {coupon && (
            <div className="flex justify-between text-[14px] text-[#49392F] mb-2">
              <span>Desconto ({coupon.code})</span><span className="text-[#C2A064]">- {formatBRL(coupon.discount_amount)}</span>
            </div>
          )}
          <div className="hairline my-3" />
          <div className="flex justify-between text-[16px] font-medium text-[#49392F] mb-5">
            <span>Total</span><span data-testid="checkout-total">{formatBRL(total)}</span>
          </div>
          <button type="submit" disabled={submitting} className="duo-btn duo-btn-primary w-full disabled:opacity-50" data-testid="place-order-button">
            {submitting ? "Enviando..." : "Confirmar pedido"}
          </button>
          <p className="text-[11px] text-[#49392F]/60 mt-3 text-center">
            O recibo em PDF fica disponível na sua área após finalizar o pedido.
          </p>
        </aside>
      </form>
    </div>
  );
}
